import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";

const Logo = () => (
  <Link to="/" className="navbar-logo">
    <img
      src="/images/logo.png"
      alt="Food and Nutrition Foundation"
      className="logo-img"
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
    <span className="logo-text">
      <span className="logo-name">Food &amp; Nutrition</span>
      <span className="logo-tagline">Foundation</span>
    </span>
  </Link>
);

const ChevronDown = ({ open }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s",
    }}
  >
    <path
      d="M3 5l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HamburgerIcon = ({ open }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    {open ? (
      <path
        d="M6 6l12 12M6 18L18 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    ) : (
      <>
        <path
          d="M4 6h16M4 12h16M4 18h16"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </>
    )}
  </svg>
);

const aboutLinks = [
  { to: "/about", label: "About Us" },
  { to: "/what-we-do", label: "What We Do" },
  { to: "/where-we-are-located", label: "Where We Are Located" },
  { to: "/our-impact", label: "Our Impact" },
  { to: "/get-involved", label: "Get Involved" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false); // desktop hover
  const [mobileAbout, setMobileAbout] = useState(false); // mobile accordion

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileAbout(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close desktop dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileAbout(false);
  };

  return (
    <nav
      className={`navbar${scrolled ? " scrolled" : ""}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container navbar-inner">
        <Logo />

        {/* DESKTOP LINKS */}
        <div className="navbar-links desktop-links">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              "nav-link" + (isActive ? " active" : "")
            }
          >
            Home
          </NavLink>

          {/* Hover-safe dropdown — CSS bridge closes the gap */}
          <div
            className={`nav-dropdown${aboutOpen ? " open" : ""}`}
            ref={dropdownRef}
            onMouseEnter={() => setAboutOpen(true)}
            onMouseLeave={() => setAboutOpen(false)}
          >
            <button
              className="nav-link dropdown-trigger"
              onClick={() => setAboutOpen((o) => !o)}
              aria-expanded={aboutOpen}
              aria-haspopup="true"
            >
              About <ChevronDown open={aboutOpen} />
            </button>

            <div className="dropdown-bridge" aria-hidden="true" />

            <div className="dropdown-menu" role="menu">
              {aboutLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="dropdown-item"
                  role="menuitem"
                  onClick={() => setAboutOpen(false)}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </div>

          <NavLink
            to="/gallery"
            className={({ isActive }) =>
              "nav-link" + (isActive ? " active" : "")
            }
          >
            Gallery
          </NavLink>
          <NavLink
            to="/staff"
            className={({ isActive }) =>
              "nav-link" + (isActive ? " active" : "")
            }
          >
            Team
          </NavLink>
          <NavLink
            to="/board"
            className={({ isActive }) =>
              "nav-link" + (isActive ? " active" : "")
            }
          >
            Board
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              "nav-link" + (isActive ? " active" : "")
            }
          >
            Contact
          </NavLink>
        </div>

        <button
          className="btn btn-primary btn-sm donate-cta"
          onClick={() => navigate("/donate")}
        >
          Donate Now
        </button>

        {/* HAMBURGER */}
        <button
          className="hamburger"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          <HamburgerIcon open={mobileOpen} />
        </button>
      </div>

      {/* MOBILE DRAWER */}
      <div
        className={`mobile-drawer${mobileOpen ? " open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <nav className="mobile-nav">
          <NavLink to="/" end className="mobile-nav-link" onClick={closeMobile}>
            Home
          </NavLink>

          {/* Mobile accordion */}
          <div className="mobile-accordion">
            <button
              className="mobile-nav-link mobile-accordion-trigger"
              onClick={() => setMobileAbout((o) => !o)}
              aria-expanded={mobileAbout}
            >
              About <ChevronDown open={mobileAbout} />
            </button>
            <div
              className={`mobile-accordion-body${mobileAbout ? " open" : ""}`}
            >
              {aboutLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="mobile-sub-link"
                  onClick={closeMobile}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </div>

          <NavLink
            to="/gallery"
            className="mobile-nav-link"
            onClick={closeMobile}
          >
            Gallery
          </NavLink>
          <NavLink
            to="/staff"
            className="mobile-nav-link"
            onClick={closeMobile}
          >
            Team
          </NavLink>
          <NavLink
            to="/board"
            className="mobile-nav-link"
            onClick={closeMobile}
          >
            Board
          </NavLink>
          <NavLink
            to="/contact"
            className="mobile-nav-link"
            onClick={closeMobile}
          >
            Contact
          </NavLink>

          <button
            className="btn btn-primary mobile-donate-btn"
            onClick={() => {
              navigate("/donate");
              closeMobile();
            }}
          >
            Donate Now
          </button>
        </nav>
      </div>

      {mobileOpen && (
        <div
          className="mobile-overlay"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
    </nav>
  );
}
